import { node, text } from "~/util/dom";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { v4 as uuidv4 } from "uuid";
import { createDOMElements } from "~/ui/deepl";

export interface IReviewDeepLData {
	text: string;
	reviewId: number;
	lang: string;
	container: HTMLElement;
	type: "text" | "reply" | "positives" | "negatives";
	native: boolean;
}

interface ITranslateData {
	posible: boolean;
	translatedText?: string;
}

interface IResponse {
	code: number;
	message: string;
	text?: string;
	reply?: string;
}

function callRequest(reviewId: number, lang: string) {
	const REQUEST_URL = `${CONFIG.requestUrl}/translate/review/${reviewId}/${lang}`;

	return fetch(REQUEST_URL, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		method: "GET",
	});
}

function translateTexts(reviewId = 0, lang: string): Promise<IResponse> {
	if (reviewId) {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise(async (resolve, reject) => {
			const response = await callRequest(reviewId, lang || Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang);

			if (response.status === 200) {
				const json = await response.json();

				json ? resolve(json) : reject();
			} else {
				reject();
			}
		});
	}

	return Promise.reject();
}

function switchTranslate(data: IReviewDeepLData, translateData: ITranslateData, elements: {
	button: HTMLButtonElement,
	origButton: HTMLButtonElement,
	translateButton: HTMLButtonElement,
	container: HTMLParagraphElement,
 }, orig: boolean, native: boolean) {
	if (data.container) {
		data.container.innerText = native
			? orig
				? translateData.translatedText
				: data.text
			: orig
				? data.text
				: translateData.translatedText;
	}

	elements.container.innerHTML = "";

	if (orig) {
		elements.container.appendChild(elements.translateButton);
		elements.container.appendChild(text(" · "));
		elements.container.appendChild(elements.origButton);
	} else {
		elements.container.appendChild(elements.button);
	}
}

export function createDeepLTranslate(data: IReviewDeepLData, type = "", changeCb?: (translate?: boolean) => void): HTMLParagraphElement {
	const { container, button, origButton, translateButton } = createDOMElements();
	const translateData: ITranslateData = {
		posible: data.lang && data.lang !== (Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang),
	};
	const reviewId = translateData.posible && data.reviewId ? data.reviewId : 0;
	const showButton = reviewId && data.lang !== (Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang);
	const loading = node("span", {}, _("deepl.loading"));
	const showOtherlanguage = async (original: boolean) => {
		container.innerHTML = "";
		container.appendChild(loading);

		try {
			if (!translateData.translatedText) {
				const translatedText = await translateTexts(reviewId, data.native ? undefined : data.lang);

				if (translatedText[data.type]) {
					if ((data.type === "negatives" || data.type === "positives") && Array.isArray(translatedText[data.type])) {
						const transformCb = arrayStr => {
							let outputStr = arrayStr.join(", ");

							// odstraneni duplicitnich carek
							outputStr = outputStr.replace(/,\s*,/ug, ",");

							return outputStr;
						};

						// eslint-disable-next-line require-atomic-updates
						translateData.translatedText = transformCb(translatedText[data.type]);
					} else {
						// eslint-disable-next-line require-atomic-updates
						translateData.translatedText = translatedText[data.type];
					}
				}

				Mapy.getComponent("logger").log("translate", {
					type,
				});
			}

			switchTranslate(data, translateData, {
				button,
				origButton,
				translateButton,
				container,
			}, original, data.native);

			changeCb && changeCb(true);
		} catch (exception) {
			// eslint-disable-next-line no-console
			console.log("DeepL translate error:", exception);
			container.innerHTML = "";
			if (original) {
				container.appendChild(button);
			} else {
				container.appendChild(translateButton);
				container.appendChild(text(" · "));
				container.appendChild(origButton);
			}
		}
	};

	if (showButton) {
		// pokud je text v rodnem jazyce, zobrazime tlacitko prelozit
		if (data.native) {
			container.appendChild(button);
			button.addEventListener("click", () => {
				showOtherlanguage(data.native);
			}, false);
			origButton.addEventListener("click", () => {
				switchTranslate(data, translateData, {
					button,
					origButton,
					translateButton,
					container,
				}, false, data.native);

				changeCb && changeCb();
			});
		// pokud neni v rodnem jazyce, zobrazime info o automatickem prekladu a tlacitka puvodniho jazyka
		} else {
			container.appendChild(translateButton);
			container.appendChild(text(" · "));
			container.appendChild(origButton);
			origButton.addEventListener("click", () => {
				showOtherlanguage(data.native);
			}, false);
			button.addEventListener("click", () => {
				switchTranslate(data, translateData, {
					button,
					origButton,
					translateButton,
					container,
				}, true, data.native);

				changeCb && changeCb();
			});
		}

		return container;
	}

	return null;
}
